import { useState, type FormEvent } from 'react';
import { MapPin } from 'lucide-react';
import { Navigate, useLocation } from 'react-router';
import { PASSWORD_MIN_LENGTH, TOTP_DIGITS } from '@findmy/shared';
import { ApiRequestError } from '../api/client';
import { useAuthConfig, useLogin, useLoginCode, useRegister, useSession } from '../api/queries';
import { Button, ErrorText } from '../components/ui';

const inputClass =
  'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800';

/**
 * The second step for accounts with two-factor login: the current code from the
 * authenticator app, or a recovery code.
 */
function CodeStep({ ticket, onRestart }: { ticket: string; onRestart: (message?: string) => void }) {
  const loginCode = useLoginCode();
  const [recovery, setRecovery] = useState(false);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const code = String(new FormData(event.currentTarget).get('code'));
    loginCode.mutate(
      { ticket, code },
      {
        onError: (err) => {
          if (err instanceof ApiRequestError && err.code === 'ticket_expired') onRestart(err.message);
        },
      },
    );
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {recovery
          ? 'Enter one of the recovery codes you saved when you turned on two-factor login. Each works once.'
          : `Enter the ${TOTP_DIGITS}-digit code from your authenticator app.`}
      </p>
      <label className="block text-sm font-medium">
        {recovery ? 'Recovery code' : 'Code'}
        <input
          key={recovery ? 'recovery' : 'app'}
          name="code"
          required
          autoFocus
          autoComplete="one-time-code"
          inputMode={recovery ? 'text' : 'numeric'}
          pattern={recovery ? undefined : `\\d{${TOTP_DIGITS}}`}
          maxLength={recovery ? 20 : TOTP_DIGITS}
          placeholder={recovery ? 'XXXX-XXXX-XXXX' : '123456'}
          className={`${inputClass} font-mono tracking-widest`}
        />
      </label>
      {loginCode.error && <ErrorText>{loginCode.error.message}</ErrorText>}
      <Button type="submit" variant="primary" className="w-full" disabled={loginCode.isPending}>
        Continue
      </Button>
      <div className="flex justify-between text-xs">
        <button
          type="button"
          onClick={() => setRecovery(!recovery)}
          className="text-blue-700 hover:underline dark:text-sky-400"
        >
          {recovery ? 'Use the code from your app' : 'Lost your phone? Use a recovery code'}
        </button>
        <button type="button" onClick={() => onRestart()} className="text-slate-500 hover:underline">
          Start again
        </button>
      </div>
    </form>
  );
}

export function LoginPage() {
  const { data: session } = useSession();
  const { data: authConfig } = useAuthConfig();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const login = useLogin();
  const register = useRegister();
  const mutation = mode === 'login' ? login : register;
  const locationNotice = (useLocation().state as { notice?: unknown } | null)?.notice;
  const [ticket, setTicket] = useState<string | null>(null);
  const [restartNotice, setRestartNotice] = useState<string | null>(null);
  const notice = restartNotice ?? locationNotice;

  if (session) return <Navigate to="/" replace />;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const body = { email: String(form.get('email')), password: String(form.get('password')) };
    setRestartNotice(null);
    if (mode === 'register') register.mutate(body);
    else login.mutate(body, { onSuccess: (result) => 'ticket' in result && setTicket(result.ticket) });
  };

  const canRegister = authConfig?.registrationOpen === true;

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
        <div className="mb-6 flex items-center gap-2">
          <MapPin className="h-7 w-7 text-blue-600" aria-hidden />
          <h1 className="text-xl font-semibold">Find My</h1>
        </div>

        {typeof notice === 'string' && (
          <p
            role="status"
            className={`mb-5 rounded-lg p-3 text-sm ${
              restartNotice
                ? 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
            }`}
          >
            {notice}
          </p>
        )}

        {ticket ? (
          <CodeStep
            ticket={ticket}
            onRestart={(message) => {
              setTicket(null);
              login.reset();
              setRestartNotice(message ?? null);
            }}
          />
        ) : (
          <>
            {canRegister && (
              <div
                className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 text-sm dark:bg-slate-800"
                role="tablist"
              >
                {(['login', 'register'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={mode === m}
                    onClick={() => setMode(m)}
                    className={`rounded-md py-1.5 font-medium ${mode === m ? 'bg-white shadow dark:bg-slate-700' : 'text-slate-600 dark:text-slate-400'}`}
                  >
                    {m === 'login' ? 'Log in' : 'Create account'}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block text-sm font-medium">
                Email
                <input name="email" type="email" autoComplete="email" required className={inputClass} />
              </label>
              <label className="block text-sm font-medium">
                Password
                <input
                  name="password"
                  type="password"
                  required
                  minLength={mode === 'register' ? PASSWORD_MIN_LENGTH : undefined}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  className={inputClass}
                />
                {mode === 'register' && (
                  <span className="mt-1 block text-xs font-normal text-slate-500">
                    At least {PASSWORD_MIN_LENGTH} characters. A few random words works well.
                  </span>
                )}
              </label>
              {mutation.error && <ErrorText>{mutation.error.message}</ErrorText>}
              <Button type="submit" variant="primary" className="w-full" disabled={mutation.isPending}>
                {mode === 'login' ? 'Log in' : 'Create account'}
              </Button>
            </form>

            {canRegister && mode === 'login' && (
              <p className="mt-4 text-xs text-slate-500">
                No account yet? The first account created on this server becomes the owner.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
